import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { RecruitmentService } from '../../service/RecruitmentService';
import GeneralUtils from '../../utilities/GeneralUtils';
import { InputTextarea } from 'primereact/inputtextarea';

const RecruitmentPlanInvalidate = (props) => {
    let emptyRecruitmentPlan = {
        id: null,
        invalidationReason: '',
    };

    const [recruitmentPlan, setRecruitmentPlan] = useState(null);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const toast = useRef(null);

    useEffect(() => {
        let _recruitmentPlan = GeneralUtils.emptyInNullForObjectFields(props?.value);
        setRecruitmentPlan(_recruitmentPlan);

        // console.log("_recruitmentPlan: ", _recruitmentPlan)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveDetails = (data) => {
        recruitmentService.invalidateRecruitmentPlan(data).then(
            (response) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(response.recruitmentPlan);
                }
                hideDialog()
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: recruitmentPlan ? recruitmentPlan : emptyRecruitmentPlan,
        validate: (data) => {
            let errors = {};

            if (!data.invalidationReason || !data.invalidationReason.trim()) {
                errors.invalidationReason = translatedMessage("form.error.institution.responsibleName.required");
            }
            
            return errors;
        },
        onSubmit: (data) => {
            // console.log("save institution details: ", data)
            saveDetails(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const dialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('recruitment.plan.invalidate')} icon="pi pi-times-circle" className="p-button p-button-danger" form="invalidation-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={props?.visible}
                header={translatedMessage('recruitment.plan.invalidation.title')}
                modal
                resizable={false}
                className="pcn-dialog p-fluid"
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <form id="invalidation-form" onSubmit={formik.handleSubmit}>
                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputTextarea
                                type="text"
                                id="invalidationReason"
                                name="invalidationReason"
                                value={formik.values.invalidationReason}
                                onChange={formik.handleChange}
                                className={classNames({ 'p-invalid': isFormFieldValid('invalidationReason') })}
                                rows={10}
                                autoResize
                            />
                            <label htmlFor="invalidationReason" className={classNames({ 'p-error': isFormFieldValid('invalidationReason') })}>
                                {translatedMessage("recruitment.plan.invalidationReason")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('invalidationReason')}                
                </form>
            </Dialog>

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    dialogTitle={translatedMessage("recruitment.plan.save.error.title")}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default RecruitmentPlanInvalidate;
